import { MarkdownTextPrimitive } from "@assistant-ui/react-markdown";
import { type ComponentProps, useContext } from "react";
import { CopilotPartRoleContext } from "@/features/copilot/components/copilot-part-role-context";
import {
  COPILOT_MARKDOWN_ELEMENTS,
  safeCopilotHref,
} from "@/features/copilot/lib/copilot-markdown-policy";

function SafeLink(props: ComponentProps<"a">) {
  const href = props.href ? safeCopilotHref(props.href, window.location.origin) : null;
  if (!href) return <span>{props.children}</span>;
  return (
    <a href={href} rel="noreferrer" className="text-primary underline underline-offset-4">
      {props.children}
    </a>
  );
}

// Tailwind's preflight strips list markers and no typography plugin is installed
// (`prose` is inert), so lists need their markers and indent spelled out.
export function MarkdownUnorderedList(props: ComponentProps<"ul">) {
  return <ul {...props} className="my-2 list-disc space-y-1 pl-5" />;
}

export function MarkdownOrderedList(props: ComponentProps<"ol">) {
  return <ol {...props} className="my-2 list-decimal space-y-1 pl-5" />;
}

/// Narration the model writes before a later step ("Let me check...") is progress, not the
/// answer, so it is set small and muted and the answer keeps the full voice.
export function CopilotMarkdown() {
  const interim = useContext(CopilotPartRoleContext) === "interim";
  return (
    <MarkdownTextPrimitive
      skipHtml
      smooth={false}
      allowedElements={COPILOT_MARKDOWN_ELEMENTS}
      components={{ a: SafeLink, ul: MarkdownUnorderedList, ol: MarkdownOrderedList }}
      className={
        interim
          ? "max-w-none text-xs leading-5 text-text-tertiary italic"
          : "prose prose-sm max-w-none text-foreground dark:prose-invert"
      }
    />
  );
}
